// Analytics + crash reporting wrappers. PostHog is the primary sink for
// product analytics (funnels, retention, cohorts). Sentry remains opt-in via
// a env-keyed stub until we're ready to wire its SDK too. Every track() also
// ships to our own /api/log so the admin dashboard's events table stays
// authoritative — the dual sink means we don't depend on a vendor for the
// learning-metrics dashboards while still using PostHog's UI for product
// analytics.

import posthog from 'posthog-js';
import { log } from './log';

interface PosthogLike {
  capture: (event: string, props?: Record<string, unknown>) => void;
  identify: (userId: string, props?: Record<string, unknown>) => void;
}

interface SentryLike {
  captureException: (err: unknown, ctx?: Record<string, unknown>) => void;
}

let posthogAdapter: PosthogLike | null = null;
let sentry: SentryLike | null = null;

let initialised = false;

export async function initObservability(): Promise<void> {
  if (initialised) return;
  initialised = true;

  const phKey = import.meta.env.VITE_POSTHOG_KEY;
  if (phKey) {
    posthog.init(phKey, {
      api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com',
      // SPA → we fire AppOpened explicitly; PostHog's auto pageview would
      // double-count and pollute funnels.
      capture_pageview: false,
      // 15 named events already cover the core flows. Autocapture adds noise
      // that makes funnels harder to define; flip on later if needed.
      autocapture: false,
      // Privacy default. Chats are personal; turn on later if a debugging
      // need justifies it (will also need privacy-notice copy).
      disable_session_recording: true,
      persistence: 'localStorage+cookie',
      loaded: (ph) => {
        if (import.meta.env.DEV) ph.debug();
      },
    });
    posthogAdapter = {
      capture: (event, props) => posthog.capture(event, props),
      identify: (userId, props) => posthog.identify(userId, props),
    };
  } else if (import.meta.env.DEV) {
    // No key in dev — fall back to console so events are still visible while
    // working without a PostHog project configured.
    posthogAdapter = {
      capture: (event, props) => console.info('[analytics]', event, props ?? {}),
      identify: (userId, props) => console.info('[analytics] identify', userId, props ?? {}),
    };
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (dsn) {
    sentry = devLogSentry;
  }
}

const devLogSentry: SentryLike = {
  captureException: (err, ctx) => {
    if (import.meta.env.DEV) console.error('[sentry] capture', err, ctx ?? {});
  },
};

export function track(event: string, props?: Record<string, unknown>): void {
  posthogAdapter?.capture(event, props);
  log(event, props);
}

export function identify(userId: string, props?: Record<string, unknown>): void {
  posthogAdapter?.identify(userId, props);
}

export function reportError(err: unknown, ctx?: Record<string, unknown>): void {
  sentry?.captureException(err, ctx);
  if (import.meta.env.DEV) console.error('[reportError]', err, ctx);
}

// Named events used across the app — keeps tracker calls discoverable.
// Names are also the event_type strings stored in the events table; rename
// either side and you orphan rows in the dashboard. The admin queries module
// references many of these directly.
export const Events = {
  AppOpened: 'app_opened',
  Onboarded: 'onboarded',
  SwipedRight: 'swiped_right',
  SwipedLeft: 'swiped_left',
  Matched: 'matched',
  MessageSent: 'message_sent',
  WordSaved: 'word_saved',
  WordMastered: 'word_mastered',
  StoryViewed: 'story_viewed',
  StoryReplied: 'story_replied',
  FlashcardSession: 'flashcard_session',
  // Added for the admin dashboard.
  SessionStart: 'session_start',
  SessionEnd: 'session_end',
  ReplyReceived: 'reply_received',
  FlashcardAttempt: 'flashcard_attempt',
  WordFirstEncountered: 'word_first_encountered',
} as const;
